import OnboardingTour from "features/onboarding/blocks/OnboardingTour";
import type { OnboardingStepData } from "features/onboarding/data/types";
import StepContent from "features/onboarding/elements/StepContent";
import { useDataQuery } from "hooks/useData";
import { useIsMobile } from "hooks/useIsMobile";
import React, { useState, useEffect, RefObject } from "react";

import {
  useMarkOnboardingSeen,
  useOnboardingSeenTopics,
} from "./useOnboardingSeen";

type UseOnboardingOptions = {
  topic: string;
  refs: (RefObject<HTMLElement> | undefined)[];
  condition: boolean;
  delay?: number;
  onOpen?: () => void;
  onClose?: () => void;
};

export function useOnboarding({
  topic,
  refs,
  condition,
  delay = 500,
  onOpen,
  onClose,
}: UseOnboardingOptions): {
  tourNode: React.ReactElement | null;
  seen: boolean;
  hasSteps: boolean;
  openTour: () => void;
} {
  const { isMobile } = useIsMobile();
  const onboardingData = useDataQuery<OnboardingStepData[]>("onboarding/");
  const seenTopics = useOnboardingSeenTopics();
  const markSeen = useMarkOnboardingSeen();
  const seen = seenTopics.includes(topic);
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const steps = onboardingData
    ?.filter((d) => d.topic === topic && d.number)
    .sort((a, b) => (a.number || 0) - (b.number || 0));

  const hasSteps = !!steps?.length;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMobile) return;

      if (hasSteps && !seen && condition) {
        setOpen(true);
        onOpen?.();
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [hasSteps, seen, condition, isMobile]);

  const handleClose = () => {
    setOpen(false);
    setCurrentStep(0);
    onClose?.();
  };

  const handleFinish = () => {
    setOpen(false);
    setCurrentStep(0);
    markSeen(topic);
    onClose?.();
  };

  const tourSteps = steps?.map((data, index) => ({
    title: "",
    target: () => (refs[index] as RefObject<HTMLElement>)?.current ?? null,
    cover: React.createElement(StepContent, { key: index, step: data }),
  }));

  const tourNode = hasSteps
    ? React.createElement(OnboardingTour, {
        steps: tourSteps,
        open,
        currentStep,
        onStepChange: setCurrentStep,
        onClose: handleClose,
        onFinish: handleFinish,
      })
    : null;

  const openTour = () => {
    if (isMobile) return;
    setOpen(true);
    onOpen?.();
  };

  return { tourNode, seen: !!seen, hasSteps, openTour };
}
